import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { GraduationCap, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";

export default function AdminLogin() {
  const { login, logout, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user && user.role === "admin") navigate("/admin", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") {
        logout();
        toast.error("This login is for administrators only.");
        return;
      }
      toast.success("Welcome back!");
      navigate("/admin", { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 ptg-grid-bg p-4" data-testid="admin-login-page">
      <Seo title="Admin Login" />
      <div className="w-full max-w-md rounded-3xl bg-white p-8 sm:p-10 shadow-2xl">
        <div className="flex items-center gap-3 mb-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-900 text-white"><GraduationCap className="h-7 w-7" /></div>
          <div>
            <div className="font-heading text-xl font-extrabold text-slate-900">PTG Admin</div>
            <div className="text-xs font-semibold text-sky-700 uppercase tracking-widest">Control Panel</div>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Lock className="h-5 w-5 text-blue-900" /> Sign in</h1>
        <p className="text-sm text-slate-500 mt-1">Enter your credentials to manage the website.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="admin-email" className="mb-1.5 block font-semibold text-slate-700">Email</Label>
            <Input id="admin-email" name="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin email" data-testid="login-email" required />
          </div>
          <div>
            <Label htmlFor="admin-password" className="mb-1.5 block font-semibold text-slate-700">Password</Label>
            <Input id="admin-password" name="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password" required />
          </div>
          <Button type="submit" disabled={loading} className="w-full h-11 bg-blue-900 hover:bg-blue-950" data-testid="login-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </Button>
        </form>
        <p className="mt-3 text-xs text-slate-400 text-center">You'll stay signed in on this device for 90 days.</p>
        <div className="mt-4 text-center">
          <Link to="/admin/forgot-password" className="text-sm font-semibold text-blue-900 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
        </div>
      </div>
    </div>
  );
}
